import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Menu, 
  MenuItem,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid
} from '@mui/material';
import { 
  AccountCircle, 
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Call as CallIcon,
  BarChart as AnalyticsIcon,
  PhoneForwarded as OutboundIcon,
  ExitToApp as LogoutIcon,
  People as PeopleIcon,
  Email,
  AdminPanelSettings,
  Refresh as RefreshIcon,
  Science as TestIcon,
  Feedback as FeedbackIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  BatchPrediction as BatchesIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { invitationService, authService, organizationService } from '../utils/api';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';

function Users() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Get user role for navigation and access control
  const userRole = authService.getUserRole();
  
  // Redirect viewers - they shouldn't have access to Users section
  useEffect(() => {
    if (userRole === 'viewer') {
      navigate('/dashboard');
      return;
    }
  }, [userRole, navigate]);
  
  // Form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);

  // Users table state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  // Load current organization
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
  }, []);

  // Fetch organization members
  const fetchUsers = async () => {
    if (!currentOrganization) {
      setUsersError('No organization selected');
      return;
    }

    try {
      setUsersLoading(true);
      setUsersError('');
      const data = await invitationService.getOrganizationMembers(currentOrganization.id);
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching organization members:', error);
      setUsersError(error.message || 'Failed to fetch organization members');
    } finally {
      setUsersLoading(false);
    }
  };

  // Load users when organization is available
  useEffect(() => {
    if (currentOrganization) {
      fetchUsers();
    }
  }, [currentOrganization]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    
    if (!role) newErrors.role = 'Role is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!currentOrganization) {
      setError('No organization selected');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const data = await invitationService.inviteUser(email, role, currentOrganization.id);
      
      if (data.requires_signup) {
        setSuccess(`Invitation sent successfully to ${data.invitee_email}! They will receive an email to complete their signup.`);
      } else {
        setSuccess(data.message);
      }
      
      // Reset form
      setEmail('');
      setRole('');
      setErrors({});
      
      toast.success(data.message || 'User invitation sent successfully!');
      
      // Refresh users list
      fetchUsers();
      
    } catch (error) {
      setError(error.message || 'Failed to send invitation');
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusChip = (status) => {
    if (status === 'accepted') {
      return <Chip label="Accepted" color="success" size="small" />;
    } else if (status === 'pending') {
      return <Chip label="Pending" color="warning" size="small" />;
    } else {
      return <Chip label="Direct Signup" color="default" size="small" />;
    }
  };

  const getRoleChip = (role) => {
    const roleColors = {
      'super_admin': 'error',
      'admin': 'primary',
      'moderator': 'secondary',
      'viewer': 'default'
    };
    
    const roleLabels = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'moderator': 'Moderator',
      'viewer': 'Viewer'
    };

    return (
      <Chip 
        label={roleLabels[role] || role} 
        color={roleColors[role] || 'default'} 
        size="small" 
      />
    );
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ...(currentOrganization?.analytics_enabled !== false ? [{ text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' }] : []),
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...((userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator') && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = item.text === 'Users';
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(239, 71, 111, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
            },
            '&:active': {
              transform: 'translateX(1px)'
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: 40,
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
              '.MuiListItem-root:hover &': {
                color: 'error.main'
              }
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out'
            }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Sidebar drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {currentOrganization ? `${currentOrganization.name} - Users` : 'Users Management'}
            </Typography>
            
            {/* Organization Switcher Button */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}
            
            <IconButton color="inherit" onClick={handleOpenMenu}>
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: 12, mb: 4 }}>
          <Grid container spacing={4}>
            {/* Invite User Form */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                    Send Invitation
                  </Typography>
                  
                  {success && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      {success}
                    </Alert>
                  )}
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        label="Email Address"
                        variant="outlined"
                        fullWidth
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth variant="outlined" error={!!errors.role}>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                          label="Role"
                          disabled={loading}
                          startAdornment={
                            <InputAdornment position="start">
                              <AdminPanelSettings color="primary" />
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="moderator">Moderator</MenuItem>
                          <MenuItem value="viewer">Viewer</MenuItem>
                        </Select>
                        {errors.role && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                            {errors.role}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <PeopleIcon />}
                      sx={{ 
                        py: 1.5,
                        px: 4,
                        fontWeight: 500
                      }}
                    >
                      {loading ? 'Sending Invitation...' : 'Send Invitation'}
                    </Button>
                  </form>

                  <Box sx={{ mt: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Role Permissions:</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      • <strong>Admin:</strong> Full access to all features and settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      • <strong>Moderator:</strong> Access to calls and reports, limited settings access
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • <strong>Viewer:</strong> Read-only access to calls and basic reports
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Users Table */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {currentOrganization ? `${currentOrganization.name} Members` : 'Organization Members'} ({users.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={fetchUsers}
                      disabled={usersLoading}
                    >
                      Refresh
                    </Button>
                  </Box>

                  {usersError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {usersError}
                    </Alert>
                  )}

                  {usersLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Role</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Created</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {users.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                  No members found for this organization
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            users.map((user, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {user.email}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {getRoleChip(user.organizations && user.organizations.length > 0 ? user.organizations[0].role : user.role)}
                                </TableCell>
                                <TableCell>
                                  {getStatusChip(user.invitation_status)}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(user.created_at)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Users; 